import { useState, type FormEvent, type JSX } from 'react';
import { Link } from 'react-router';
import { Button, Card, Field, Input, StatusIndicator } from '@viniciomendesr/rayrovisk-design-system';
import { requestPasswordReset } from '../api/password';
import { AuthLayout, AuthText } from './PasswordScreenParts';

/**
 * Asking for a reset link.
 *
 * The same silence the sign-in link screen keeps, for the same reason: the
 * answer must not depend on whether the address has an account (§12.3), so this
 * shows one confirmation whatever the service said — including when the service
 * refused it for rate limiting.
 */
export function PasswordResetScreen(): JSX.Element {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    await requestPasswordReset(email).catch(() => undefined);
    setBusy(false);
    setSent(true);
  }

  return (
    <AuthLayout>
      <Card layer="functional" elevation="raised" padding="lg">
        {sent ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <StatusIndicator status="live" label="Link enviado" detail="vale 15 minutos" />
            <h1 style={{ margin: 0, font: '400 24px/1.15 var(--rvk-font-expressive)' }}>
              Confira o seu e-mail.
            </h1>
            <AuthText>
              Se existir uma conta nesse endereço, o link para escolher uma senha nova chega em
              instantes. Não esqueça de olhar a caixa de promoções.
            </AuthText>
            <Button variant="ghost" size="sm" onClick={() => setSent(false)}>
              Usar outro e-mail
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <h1 style={{ margin: 0, font: '400 26px/1.15 var(--rvk-font-expressive)' }}>
                Recuperar a senha
              </h1>
              <AuthText>
                A gente manda um link para você escolher uma senha nova. Enquanto isso, dá para
                entrar pelo link de acesso de sempre.
              </AuthText>
            </div>

            <Field label="E-mail da conta" hint="O mesmo que você usou na compra.">
              <Input
                type="email"
                required
                autoComplete="email"
                placeholder="voce@email.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <Button type="submit" variant="primary" fullWidth loading={busy}>
              Enviar link de recuperação
            </Button>

            <div style={{ height: 1, background: 'var(--rvk-border-subtle)' }} />

            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <Link to="/entrar">
                <Button variant="ghost" size="sm" type="button">
                  Entrar com link por e-mail
                </Button>
              </Link>
            </div>
          </form>
        )}
      </Card>
    </AuthLayout>
  );
}
